import React from 'react';

/**
 * ErrorBoundary catches render-time crashes anywhere in the React tree
 * so the user sees a usable error message instead of a blank white page.
 * It also gives them a Reload button rather than forcing a re-login.
 */
class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null, info: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, errorInfo) {
    // Preserve the React component stack so we can see *where* it crashed.
    this.setState({ info: errorInfo });
    // eslint-disable-next-line no-console
    console.error('UI crash caught by ErrorBoundary:', error, errorInfo);
  }

  handleReload = () => {
    this.setState({ hasError: false, error: null, info: null });
    window.location.reload();
  };

  handleCopy = async () => {
    const text = [
      'Error: ' + (this.state.error?.message || String(this.state.error)),
      'Stack: ' + (this.state.error?.stack || ''),
      'Component Stack: ' + (this.state.info?.componentStack || '')
    ].join('\n\n');
    try {
      await navigator.clipboard.writeText(text);
      // eslint-disable-next-line no-alert
      alert('Error details copied to clipboard.');
    } catch {
      // eslint-disable-next-line no-console
      console.log(text);
    }
  };

  render() {
    if (this.state.hasError) {
      const msg = this.state.error?.message || String(this.state.error || '');
      const stack = this.state.error?.stack || '';
      const componentStack = this.state.info?.componentStack || '';
      return (
        <div className="min-h-screen flex items-center justify-center bg-slate-50 p-4" data-testid="error-boundary">
          <div className="max-w-2xl w-full bg-white rounded-xl shadow-lg p-6 border border-red-100">
            <h2 className="text-lg font-bold text-red-700 mb-2">Something went wrong on this page</h2>
            <p className="text-sm text-slate-600 mb-4">
              Don't worry — you're still signed in. Reload to continue, and please send me the details below so I can fix the root cause.
            </p>
            <details open className="mb-3">
              <summary className="text-xs font-semibold text-slate-700 cursor-pointer">Error message</summary>
              <pre className="text-xs bg-slate-100 rounded p-2 mt-1 whitespace-pre-wrap break-all text-slate-700">
                {msg}
              </pre>
            </details>
            {componentStack && (
              <details className="mb-3">
                <summary className="text-xs font-semibold text-slate-700 cursor-pointer">Component stack (where it crashed)</summary>
                <pre className="text-[11px] bg-slate-100 rounded p-2 mt-1 whitespace-pre-wrap break-all text-slate-700 max-h-48 overflow-y-auto">
                  {componentStack}
                </pre>
              </details>
            )}
            {stack && (
              <details className="mb-4">
                <summary className="text-xs font-semibold text-slate-700 cursor-pointer">JS stack trace</summary>
                <pre className="text-[11px] bg-slate-100 rounded p-2 mt-1 whitespace-pre-wrap break-all text-slate-700 max-h-48 overflow-y-auto">
                  {stack}
                </pre>
              </details>
            )}
            <div className="flex gap-2">
              <button
                type="button"
                onClick={this.handleCopy}
                data-testid="error-boundary-copy"
                className="flex-1 bg-slate-200 hover:bg-slate-300 text-slate-800 py-2 rounded-lg font-medium text-sm"
              >
                Copy details
              </button>
              <button
                type="button"
                onClick={this.handleReload}
                data-testid="error-boundary-reload"
                className="flex-1 bg-slate-900 hover:bg-slate-800 text-white py-2 rounded-lg font-medium"
              >
                Reload page
              </button>
            </div>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
